import { useState, type CSSProperties } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Logo } from '@/components/brand/Logo';
import { CountUp } from '@/components/roi/CountUp';
import { GovernedCaseRail } from '@/components/pitch-v5/GovernedCaseRail';
import { useGovernedCaseSequence } from '@/components/pitch-v5/useGovernedCaseSequence';
import { EASE_EXPO, maskReveal, revealGroup, revealItem } from '@/lib/motion';
import { UK_TAM_HEADLINE_ARR } from '@/lib/roi/market-data';

/**
 * Cinematic opener for the investor run-through.
 *
 * Full-viewport height. Wordmark top-left, sticky-feeling top frame.
 * Ambient brand glow drifting behind the headline. Headline + sub-deck
 * + three stat call-outs with count-up. Scroll affordance bottom-center.
 */
export function PitchHero({
  investorCaseFlow = false,
}: {
  investorCaseFlow?: boolean;
}) {
  const reduced = useReducedMotion() ?? false;

  return (
    <section
      id="hero"
      className="relative isolate flex min-h-[100svh] w-full flex-col overflow-hidden"
    >
      <AmbientGlow />

      <header className="relative z-20 mx-auto flex w-full max-w-6xl items-center justify-between px-6 pt-8 sm:px-10 sm:pt-10">
        <Logo size="sm" />
        <a
          href="/roi"
          className="hidden text-[12px] font-medium uppercase tracking-[0.18em] text-ink-muted transition-colors hover:text-ink-strong sm:inline-flex sm:items-center sm:gap-2"
        >
          ROI model
          <span aria-hidden>→</span>
        </a>
      </header>

      <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center gap-12 px-6 py-24 sm:px-10 sm:py-28">
        <div
          className={
            investorCaseFlow
              ? 'grid items-center gap-10 lg:grid-cols-[minmax(0,0.92fr)_minmax(28rem,1.08fr)]'
              : ''
          }
        >
          <div className="flex min-w-0 flex-col gap-12">
            {/* The opener used to arrive as one rigid slab on a single tween.
            Now the badge, each headline line, the tagline and the body
            land in reading order — the two headline lines rising out from
            behind their own baselines. Same words, but the eye is walked
            down the page instead of being handed a finished block. */}
            <motion.div
              variants={revealGroup(0.12, 0.1)}
              initial="hidden"
              animate="visible"
              className="flex flex-col gap-8"
            >
              <motion.span
                variants={revealItem(12, 0.6)}
                className="inline-flex w-fit items-center gap-3 rounded-full border border-accent-blue-border bg-accent-blue-bg px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-bright"
              >
                <span
                  aria-hidden
                  className="block h-1.5 w-1.5 rounded-full bg-brand-bright"
                />
                Investor run through
              </motion.span>

              <h1
                className={`max-w-[18ch] text-balance text-[56px] font-semibold leading-[0.98] tracking-[-0.02em] text-ink-strong sm:text-[80px] ${
                  investorCaseFlow
                    ? 'lg:text-[64px] xl:text-[72px]'
                    : 'lg:text-[96px]'
                }`}
              >
                <motion.span
                  variants={maskReveal(reduced, 1)}
                  className="block pb-[0.06em]"
                >
                  Asset Hound
                </motion.span>
                <motion.span
                  variants={maskReveal(reduced, 1)}
                  className="block pb-[0.06em] text-brand-bright"
                >
                  Automating insolvency
                </motion.span>
              </h1>

              <motion.p
                variants={revealItem(16, 0.7)}
                className="max-w-2xl text-balance text-[20px] font-medium text-brand-bright sm:text-[24px]"
              >
                Confidence through intelligence
              </motion.p>

              <motion.p
                variants={revealItem(16, 0.7)}
                className="max-w-2xl text-balance text-[18px] leading-relaxed text-ink-muted sm:text-[20px]"
              >
                Every UK insolvency case today runs on email, spreadsheets and
                decade old case systems. Asset Hound replaces the manual
                document, email and reporting workflow with AI, keeping the
                practitioner in control of every decision and every line of
                statutory output
              </motion.p>
            </motion.div>

            {!investorCaseFlow ? <HeroStats /> : null}
          </div>

          {investorCaseFlow ? (
            <InvestorCaseFlow reducedMotion={reduced} />
          ) : null}

          {investorCaseFlow ? <HeroStats wide /> : null}
        </div>
      </div>

      <motion.a
        href="#signal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.9 }}
        className="group relative z-10 mx-auto mb-10 flex flex-col items-center gap-2 text-[11px] font-medium uppercase tracking-[0.22em] text-ink-dim transition-colors hover:text-ink-strong sm:mb-14"
      >
        <span>Scroll</span>
        <motion.span
          aria-hidden
          className="block h-8 w-px bg-gradient-to-b from-brand-bright/80 to-transparent"
          animate={{ scaleY: [0.4, 1, 0.4], opacity: [0.4, 1, 0.4] }}
          style={{ transformOrigin: 'top' }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.a>
    </section>
  );
}

function HeroStats({ wide = false }: { wide?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: EASE_EXPO, delay: 0.85 }}
      className={`pitch-strip grid w-full grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border-subtle lg:grid-cols-[0.9fr_1.05fr_1.25fr] ${
        wide ? 'lg:col-span-2 lg:max-w-4xl lg:justify-self-start' : 'max-w-3xl'
      }`}
    >
      <HeroStat
        label="UK TAM"
        value={UK_TAM_HEADLINE_ARR}
        prefix="£"
        suffix="m"
        duration={1.4}
        decimals={0}
      />
      <HeroStat
        label="2025 E&W insolvencies"
        value={23942}
        duration={1.8}
        format="thousands"
      />
      <HeroStat
        label="Active appointment takers"
        value={1262}
        duration={1.6}
        format="comma"
      />
    </motion.div>
  );
}

interface NavigatorWithConnection extends Navigator {
  connection?: { saveData?: boolean };
}

function InvestorCaseFlow({ reducedMotion }: { reducedMotion: boolean }) {
  const [saveData] = useState(() => {
    if (typeof navigator === 'undefined') return false;
    return Boolean((navigator as NavigatorWithConnection).connection?.saveData);
  });
  const sequence = useGovernedCaseSequence({
    reducedMotion,
    saveData,
    autoReleaseMs: 1_800,
  });

  return (
    <div data-v1-case-phase={sequence.phase} data-v5-governed-case-rail="">
      <div
        className="hidden min-w-0 lg:block"
        style={
          {
            '--v5-ink': '#F8FAFC',
            '--v5-muted': '#94A3B8',
            '--v5-blue': '#3B82F6',
            '--v5-blue-bright': '#60A5FA',
            '--v5-amber': '#FBBF24',
            '--v5-panel': '#0F172A',
          } as CSSProperties
        }
      >
        <GovernedCaseRail phase={sequence.phase} variant="investor-case" />
      </div>
      <MobileGovernedCaseStrip />
    </div>
  );
}

function MobileGovernedCaseStrip() {
  return (
    <div
      className="grid grid-cols-[1fr_auto_auto] items-center gap-3 rounded-2xl border border-border bg-[#0F172A]/70 px-4 py-4 lg:hidden"
      role="img"
      aria-label="Insolvency legislation passes through Asset Hound and practitioner approval into one governed case file"
    >
      <div className="min-w-0 space-y-2">
        <p className="text-[11px] font-semibold leading-tight text-ink-strong">
          Insolvency Act 1986
        </p>
        <p className="max-w-[19ch] text-[10px] font-semibold leading-tight text-ink-strong">
          Insolvency Rules 2016
        </p>
      </div>
      <div className="flex items-center" aria-hidden>
        <img
          src="/brand/asset-hound-icon.png"
          alt=""
          className="h-12 w-12 object-contain"
        />
        <span className="h-1 w-4 rounded-full bg-brand-bright" />
        <span className="grid h-9 w-9 place-items-center rounded-full border-2 border-[#FBBF24] bg-[#FBBF24]/10">
          <span className="h-2 w-2 rounded-full bg-[#FBBF24]" />
        </span>
        <span className="h-1 w-4 rounded-full bg-brand-bright" />
      </div>
      <div
        className="relative h-14 w-11 border border-brand-bright/80 bg-[#0B1220]"
        aria-hidden
      >
        <span className="absolute left-2 top-3 h-px w-6 bg-brand-bright/70" />
        <span className="absolute left-2 top-6 h-px w-5 bg-ink-muted/70" />
        <span className="absolute left-2 top-9 h-px w-6 bg-ink-muted/70" />
        <span className="absolute bottom-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-[#FBBF24]" />
      </div>
    </div>
  );
}

function HeroStat({
  label,
  value,
  prefix = '',
  suffix = '',
  duration = 1.4,
  format = 'plain',
  decimals,
}: {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  /** plain = "100", thousands = "23,938", comma = adds thousands separators. */
  format?: 'plain' | 'thousands' | 'comma';
  /** Fixed decimal places for figures such as the £77.9m UK TAM. */
  decimals?: number;
}) {
  const formatter = (n: number) => {
    const rounded = Math.round(n);
    const body =
      decimals !== undefined
        ? n.toFixed(decimals)
        : format === 'plain'
          ? rounded.toString()
          : rounded.toLocaleString('en-GB');
    return `${prefix}${body}${suffix}`;
  };

  return (
    <div className="pitch-cell flex min-w-0 flex-col gap-2 p-6 sm:p-7">
      <span className="whitespace-nowrap text-[11px] font-medium uppercase tracking-[0.22em] text-ink-dim">
        {label}
      </span>
      <span className="font-semibold tabular-nums text-ink-strong [font-size:36px] [line-height:1] sm:[font-size:44px]">
        <CountUp
          target={value}
          duration={duration}
          delay={0.6}
          formatter={formatter}
        />
      </span>
    </div>
  );
}

/**
 * Background ambient glow — two soft brand-blue blurs drifting at different
 * cadences. Same recipe as Hero/Contentious cards on /roi, scaled to a
 * cinematic hero footprint.
 */
function AmbientGlow() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
      <motion.div
        className="absolute left-1/2 top-[-10%] h-[640px] w-[1500px] -translate-x-1/2 rounded-full opacity-[0.22] blur-[140px]"
        style={{ background: '#3B82F6' }}
        animate={{ opacity: [0.18, 0.26, 0.18] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute left-[20%] top-[40%] h-[420px] w-[820px] rounded-full opacity-[0.10] blur-[120px]"
        style={{ background: '#60A5FA' }}
        animate={{ opacity: [0.06, 0.14, 0.06], x: [0, 30, 0] }}
        transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
}
